import type { RouteLocationRaw, Router } from 'vue-router'
import type { Notification } from '../types/notifications.types'

/**
 * Куда вести по клику на уведомление. Единый источник навигации для панели
 * (NotificationPanel) и страницы (NotificationsView), чтобы поведение не расходилось.
 *
 * Для сообщений мессенджера (`unread_message`) открываем чат в карточке клиента на
 * нужном провайдере и, если бэкенд прислал `message_id`, просим ленту прокрутиться к
 * этому сообщению и подсветить его (см. ChatWindow `highlight-message-id`).
 * Метаданные `provider` / `client_id` / `message_id` кладёт messenger-providers.
 *
 * Возвращает `null`, если из уведомления некуда переходить.
 */
export function buildNotificationRoute(notification: Notification): RouteLocationRaw | null {
  const meta = notification.metadata ?? {}
  const provider = meta.provider as string | undefined

  if (notification.notificationType === 'unread_message' && provider) {
    const clientId = meta.client_id as string | undefined
    // Чат без привязанного клиента показать в карточке нельзя — уводим на список.
    if (!clientId) return '/dashboard/notifications'

    const messageId = meta.message_id as string | undefined
    const query: Record<string, string> = { tab: 'chats', provider }
    if (messageId) query.message = messageId
    return { path: `/dashboard/clients/${clientId}`, query }
  }

  // Прочие типы — по явному URL из metadata, если он есть.
  const url = meta.url as string | undefined
  return url ?? null
}

/**
 * Переход по уведомлению. Возвращает `true`, если куда-то ушли.
 *
 * Внешние ссылки (файл выгрузки в Google Drive, DEV-1129) открываем новой вкладкой:
 * `router.push` принял бы абсолютный URL за внутренний путь и увёл бы в никуда.
 */
export function openNotificationTarget(notification: Notification, router: Router): boolean {
  const target = buildNotificationRoute(notification)
  if (!target) return false

  if (typeof target === 'string' && /^https?:\/\//i.test(target)) {
    window.open(target, '_blank', 'noopener')
    return true
  }

  router.push(target)
  return true
}
